
import { useState } from "react";
import { Upload, Trash2 } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

interface UploadDialogProps {
  isUploading: boolean;
  hasAvatar: boolean;
  onUrlUpdate: (url: string) => Promise<void>;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  onRemove: () => Promise<void>;
  onImportFromIdentity?: () => Promise<void>;
}

export function UploadDialog({ 
  isUploading, 
  hasAvatar, 
  onUrlUpdate, 
  onFileUpload, 
  onRemove,
  onImportFromIdentity,
}: UploadDialogProps) {
  const [avatarUrlInput, setAvatarUrlInput] = useState('');

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Update Profile Picture</DialogTitle>
        <DialogDescription>
          Choose a new profile picture by uploading an image or entering a URL.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        {onImportFromIdentity && (
          <div className="space-y-2">
            <Button 
              variant="secondary" 
              onClick={onImportFromIdentity}
              disabled={isUploading}
              className="w-full"
            >
              Import from identity
            </Button>
          </div>
        )}
        
        <div className="space-y-2">
          <label className="text-sm font-medium">Picture URL</label>
          <Input
            type="text"
            placeholder="Enter picture URL"
            value={avatarUrlInput}
            onChange={(e) => setAvatarUrlInput(e.target.value)}
          />
          <Button 
            onClick={() => onUrlUpdate(avatarUrlInput)}
            disabled={isUploading || !avatarUrlInput.trim()}
            className="w-full"
          >
            {isUploading ? (
              <>
                <Spinner size="sm" className="mr-2" ariaLabel="Updating" />
                Updating...
              </>
            ) : (
              'Update URL'
            )}
          </Button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              Or
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <input
            type="file"
            accept="image/*"
            id="profile-picture-upload"
            onChange={onFileUpload}
            disabled={isUploading}
            className="hidden"
          />
          <label htmlFor="profile-picture-upload">
            <Button 
              variant="outline" 
              className="w-full cursor-pointer" 
              disabled={isUploading}
              asChild
            >
              <div>
                {isUploading ? (
                  <>
                    <Spinner size="sm" className="mr-2" ariaLabel="Uploading" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Image
                  </>
                )}
              </div>
            </Button>
          </label>
        </div>

        {hasAvatar && (
          <Button
            variant="destructive"
            onClick={onRemove}
            disabled={isUploading}
            className="w-full"
          >
            {isUploading ? (
              <>
                <Spinner size="sm" className="mr-2" ariaLabel="Removing" />
                Removing...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Remove Picture
              </>
            )}
          </Button>
        )}
      </div>
    </DialogContent>
  );
}
